import { array, nonEmptyArray, record } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { sumNumbersAsDecimals } from '@mews/framework/numeric';
import { TaxValue } from '@mews-distributor/data-access';
import { PricingMode } from '@mews/enums';
import { Globalization, TaxRate } from '../../../../modules/globalization/types';
import { ReservationPrice } from '../../../../modules/entities/reservationPrices/types';
import { getTaxRates, localizeCurrencyValue } from '../../../../modules/globalization/selectors';
import { useAppSelector } from '../../../../utils/redux';

type DelocalizedTaxValue = TaxRate & TaxValue;

const groupByTaxRateCode = flow(
    nonEmptyArray.groupBy((taxRate: DelocalizedTaxValue) => taxRate.code),
    record.toArray,
    array.map(([, taxRates]) => ({
        ...taxRates[0],
        value: pipe(
            taxRates,
            array.map(({ value }) => value),
            array.reduce(0, sumNumbersAsDecimals)
        ),
    }))
);

const getReservationPricesTaxValues = (reservationPrices: ReservationPrice[]) =>
    pipe(
        reservationPrices,
        array.flatMap(({ totalAmount }) => totalAmount.taxValues)
    );

interface GetDelocalizedTaxValuesProps {
    reservationPrices: ReservationPrice[];
    taxRates?: Globalization['taxRates'];
}

const getDelocalizedTaxValues = ({ reservationPrices, taxRates }: GetDelocalizedTaxValuesProps) =>
    pipe(
        reservationPrices,
        getReservationPricesTaxValues,
        array.map(({ taxRateCode, value }) => {
            const taxRate = taxRates?.[taxRateCode];

            if (taxRate == null) {
                return null;
            }

            const taxValue = {
                ...taxRate,
                taxRateCode,
                value,
            };

            return taxValue;
        }),
        array.filter((taxValue) => taxValue != null)
    );

interface GetTaxValuesProps extends GetDelocalizedTaxValuesProps {
    currencyCode: string;
    languageCode: string;
}

export const useGetTaxValues = ({ reservationPrices, currencyCode, languageCode }: GetTaxValuesProps) => {
    const taxRates = useAppSelector(getTaxRates);
    const delocalizedTaxValues = getDelocalizedTaxValues({ reservationPrices, taxRates });

    return useAppSelector((state) =>
        pipe(
            delocalizedTaxValues,
            groupByTaxRateCode,
            array.map(({ value, ...taxValue }) => ({
                ...taxValue,
                value: localizeCurrencyValue(state, {
                    currencyValue: { value, currencyCode },
                    pricingMode: PricingMode.Simple,
                    languageCode,
                }),
            }))
        )
    );
};
